/** Helpers for reading AutoDB field values in either v1-wrapped or v2-bare shapes. */

import type { TooltipData } from "../components/AutodbTooltipWrapper";

const DEVELOPMENT_STAGE_ORDER: Record<string, number> = {
  Discovery: 0,
  "Phase 1": 1,
  "Phase 2": 2,
  "Phase 3": 3,
  "Phase 4": 4,
  Market: 5,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function autodbScalar(field: unknown): unknown {
  if (field == null) return undefined;
  if (typeof field !== "object") return field;
  if (Array.isArray(field)) return field;
  const record = field as Record<string, unknown>;
  if ("value" in record) return autodbScalar(record.value);
  return field;
}

export function formatHeadquartersAddress(company: {
  value?: { headquarters?: unknown };
}): string {
  const headquarters = company?.value?.headquarters as
    | { value?: Record<string, unknown> }
    | Record<string, unknown>
    | undefined;
  const hq =
    headquarters && "value" in headquarters
      ? (headquarters.value as Record<string, unknown> | undefined)
      : (headquarters as Record<string, unknown> | undefined);
  if (!hq) return "";

  return [
    autodbScalar(hq.city),
    autodbScalar(hq.state),
    autodbScalar(hq.country),
    autodbScalar(hq.continent),
  ]
    .filter(Boolean)
    .join(", ");
}

export function formatLatestFunding(latestFunding: unknown): string {
  const root =
    latestFunding &&
    typeof latestFunding === "object" &&
    "value" in (latestFunding as Record<string, unknown>)
      ? (latestFunding as { value?: Record<string, unknown> }).value
      : (latestFunding as Record<string, unknown> | undefined);
  if (!root) return "";

  const amount = autodbScalar(root.amount);
  if (amount == null || amount === "") return "";

  const type = autodbScalar(root.type);
  return `$${amount}` + (type ? ` (${type})` : "");
}

export function getArrayFieldItems(field: unknown): unknown[] {
  const value = autodbScalar(field);
  return Array.isArray(value) ? value : [];
}

function pipelineAssetRoot(
  asset: unknown,
): Record<string, unknown> | undefined {
  if (!isRecord(asset)) return undefined;
  if (isRecord(asset.value)) return asset.value;
  return asset;
}

function developmentStageRank(asset: unknown): number {
  const root = pipelineAssetRoot(asset);
  const stage = autodbScalar(root?.development_stage ?? root?.stage);
  if (typeof stage !== "string") return -1;
  return DEVELOPMENT_STAGE_ORDER[stage] ?? -1;
}

export function getLeadPipelineAsset(company?: {
  value?: { lead_pipeline_asset?: unknown; pipeline?: unknown };
}): Record<string, unknown> | undefined {
  const lead = company?.value?.lead_pipeline_asset;
  if (isRecord(lead)) return lead;

  const pipelineField = company?.value?.pipeline;
  const pipeline = isRecord(pipelineField) ? pipelineField : undefined;
  const pipelineRoot =
    pipeline && isRecord(pipeline.value) ? pipeline.value : pipeline;
  const assets = getArrayFieldItems(pipelineRoot?.assets);

  let bestAsset: Record<string, unknown> | undefined;
  let bestRank = -1;
  for (const asset of assets) {
    if (!isRecord(asset)) continue;
    const rank = developmentStageRank(asset);
    if (rank > bestRank) {
      bestRank = rank;
      bestAsset = asset;
    }
  }
  return bestAsset;
}

export function getTherapeuticAreas(
  leadAsset?: Record<string, unknown>,
): string[] {
  const root =
    leadAsset && "value" in leadAsset
      ? (leadAsset.value as Record<string, unknown> | undefined)
      : leadAsset;
  const taRaw = getArrayFieldItems(root?.therapeutic_area);
  return taRaw
    .map((item) => autodbScalar(item))
    .filter(
      (item): item is string => typeof item === "string" && item.length > 0,
    );
}

export function getDevelopmentStage(
  leadAsset?: Record<string, unknown>,
): string {
  const root =
    leadAsset && "value" in leadAsset
      ? (leadAsset.value as Record<string, unknown> | undefined)
      : leadAsset;
  const stage = autodbScalar(root?.development_stage ?? root?.stage);
  return typeof stage === "string" ? stage : "";
}

export function getScalarFieldValue(
  field: unknown,
): string | number | undefined {
  const value = autodbScalar(field);
  if (typeof value === "string" || typeof value === "number") return value;
  return undefined;
}

export function getStringFieldValue(field: unknown): string | undefined {
  const value = autodbScalar(field);
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Return the wrapped field object that carries tooltip provenance (source_id,
 * index_date, data_comment). v2 bare values carry no provenance, so undefined
 * is returned and the tooltip is skipped.
 */
export function getFieldInfo(
  container: unknown,
  fieldName: string,
): TooltipData | undefined {
  if (!isRecord(container)) return undefined;
  const root = isRecord(container.value) ? container.value : container;
  const field = root[fieldName];
  return isRecord(field) ? (field as TooltipData) : undefined;
}
